import * as React from 'react';

import { LodgingLinkMessage } from 'components/common/schema-types/messages/lodging-link-message';
import { PropertyHighlightSectionModel } from 'models/common/common-types';

import { UitkBadgeSize, UitkBadgeTheme, UitkMemberBadge } from 'uitk-react-badge';
import { UitkCard, UitkCardContent } from 'uitk-react-cards';
import { UitkExperimentalListItem } from 'uitk-react-experimental-list-item';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkLinkAlign } from 'uitk-react-link';
import { UitkMark, UitkMarkSize } from 'uitk-react-mark';
import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';

interface PropertyHighlightSectionProps {
  model: PropertyHighlightSectionModel;
}

export const PropertyHighlightSection: React.FC<PropertyHighlightSectionProps> = React.memo(({ model }) => {
  if (!model) {
    return null;
  }

  const { footerLink, header, subSections } = model;
  const { badge, mark } = header;
  const markId = mark?.id;
  const badgeText = badge?.text;
  const badgeTheme = badge?.theme;
  const { contents, description } = subSections[0];

  if (!contents || contents.length === 0) {
    return null;
  }

  return (
    <UitkCard className="all-l-padding-four all-r-padding-four all-t-padding-four all-b-padding-four all-b-margin-three">
      <UitkCardContent>
        <UitkLayoutGrid columns={{ small: 1, medium: 2, large: 3 }}>
          <UitkLayoutGridItem colSpan="all">
            <div>
              <p className="is-visually-hidden">VIP Access</p>
              {mark && (
                <UitkSpacing margin={{ inlineend: 'three' }}>
                  <UitkMark description="VIP Access" name={markId} size={UitkMarkSize.MEDIUM_LARGE} />
                </UitkSpacing>
              )}
              {badge && <UitkMemberBadge text={badgeText} theme={UitkBadgeTheme[badgeTheme]} size={UitkBadgeSize.LARGE} />}
            </div>
          </UitkLayoutGridItem>
          <UitkSpacing padding={{ blockstart: 'two' }} margin={{ inlineend: 'three' }}>
            <UitkLayoutGridItem colSpan={{ medium: 2, large: 1 }}>
              <UitkText>{description}</UitkText>
            </UitkLayoutGridItem>
          </UitkSpacing>
          <UitkLayoutGridItem colSpan={{ medium: 2, large: 2 }}>
            <div>
              {contents && contents.length > 0 && (
                <UitkSpacing padding={{ blockstart: 'three' }} margin={{ inlinestart: 'two' }}>
                  <UitkLayoutGrid columns={{ small: 1, medium: 2, large: 2 }} space="six" tag="ul">
                    {contents.map((content, index) => {
                      const { icon } = content;
                      const { id } = icon;
                      const { value } = content;

                      return (
                        <UitkLayoutGridItem key={`uitk-experimental-list-item-${index}`}>
                          <UitkSpacing padding={{ blockstart: 'two' }}>
                            <UitkExperimentalListItem icon={id} text={value} />
                          </UitkSpacing>
                        </UitkLayoutGridItem>
                      );
                    })}
                  </UitkLayoutGrid>
                </UitkSpacing>
              )}
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
        {footerLink && (
          <UitkLayoutFlex alignItems="end" className="uitk-cell all-t-margin-two" direction="column" wrap="nowrap">
            <UitkLayoutFlexItem>
              <LodgingLinkMessage model={footerLink} iconPos={UitkLinkAlign.RIGHT} inline={true} useAbsolutePath={true} />
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        )}
      </UitkCardContent>
    </UitkCard>
  );
});

PropertyHighlightSection.displayName = 'PropertyHighlightSection';
